import { useState, useEffect, useCallback } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Switch } from "@/shared/components/ui/switch";
import { Separator } from "@/shared/components/ui/separator";
import { Badge } from "@/shared/components/ui/badge";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/shared/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Upload } from "lucide-react";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useSettings } from "@/shared/contexts/SettingsContext";
import { useToast } from "@/shared/hooks/use-toast";
import GoogleCalendarIntegration from "@/features/integrations/GoogleCalendarIntegration";
import { supabase } from "@/integrations/supabase/client";
import { WorkingHoursSettings } from "@/features/settings/components/WorkingHoursSettings";
import { IntakeLinkCard } from "@/features/intake/components/IntakeLinkCard";
import { DEFAULT_WORKING_HOURS, UNSAVED_WORKING_HOURS, type WorkingHoursConfig } from "@/features/intake/types/workingHours";
import { loadOrCreateUserSettings, saveUserSettings } from "@/shared/services/userSettings";
import { useAuth } from "@/shared/contexts/AuthContext";

interface SettingsSection {
  id: string;
  title: string;
  content: React.ReactNode;
  hasChanges: () => boolean;
  onSave: () => void;
}

const SettingsPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Get settings from context
  const {
    scheduledCallMessage,
    setScheduledCallMessage,
    callSettingsMessage,
    setCallSettingsMessage,
    requestRecordingMessage,
    setRequestRecordingMessage,
    reminderMessage,
    setReminderMessage,
    reminderMinutes,
    setReminderMinutes,
    callAutoRecord,
    setCallAutoRecord,
    callHideSelfView,
    setCallHideSelfView,
    callUseBackCamera,
    setCallUseBackCamera,
    organizationName,
    setOrganizationName,
    systemEmail,
    setSystemEmail,
    notifyOnLinkShare,
    setNotifyOnLinkShare,
    notifyAutoRecording,
    setNotifyAutoRecording
  } = useSettings();
  
  // State management with initial values for change tracking
  const [autoReminder, setAutoReminder] = useState(true);
  const [reviewMessage, setReviewMessage] = useState("");
  const [reviewResponseMessage, setReviewResponseMessage] = useState("");
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Housecall Pro integration – API Key approach (brief Section 2: no OAuth)
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [hcpConnected, setHcpConnected] = useState(false);
  const [hcpApiKey, setHcpApiKey] = useState("");
  const [hcpSaving, setHcpSaving] = useState(false);

  // Working hours for public intake scheduling
  const [workingHours, setWorkingHours] = useState<WorkingHoursConfig>(DEFAULT_WORKING_HOURS);
  const [savedWorkingHours, setSavedWorkingHours] = useState<WorkingHoursConfig>(UNSAVED_WORKING_HOURS);
  const [workingHoursPersisted, setWorkingHoursPersisted] = useState(false);

  useEffect(() => {
    loadOrCreateUserSettings()
      .then((settings) => {
        const stored = settings.extras?.working_hours as WorkingHoursConfig | undefined;
        if (stored) {
          setWorkingHours(stored);
          setSavedWorkingHours(stored);
          setWorkingHoursPersisted(true);
        } else {
          setWorkingHours(DEFAULT_WORKING_HOURS);
          setSavedWorkingHours(UNSAVED_WORKING_HOURS);
          setWorkingHoursPersisted(false);
        }
      })
      .catch(() => {});
  }, []);

  // Load company and “already connected” status in the background (form is always shown; this only flips to “Connected” if we find a saved key)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (cancelled || !user) return;
        const { data: role } = await supabase
          .from("user_roles")
          .select("company_id")
          .eq("user_id", user.id)
          .limit(1)
          .maybeSingle();
        if (cancelled || !role?.company_id) return;
        setCompanyId(role.company_id);
        const { data: int } = await supabase
          .from("company_integrations")
          .select("id")
          .eq("company_id", role.company_id)
          .eq("provider", "housecall_pro")
          .not("api_key", "is", null)
          .maybeSingle();
        if (!cancelled && int) setHcpConnected(true);
      } catch {
        // ignore; form is always visible
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const connectHousecallPro = async () => {
    const key = hcpApiKey.trim();
    if (!key) {
      toast({ title: "Error", description: "Enter your Housecall Pro API key", variant: "destructive" });
      return;
    }
    setHcpSaving(true);
    const CONNECT_TIMEOUT_MS = 10_000; // 10s max – direct DB only; no Edge Function
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Connection timed out. Check your network and try again.")), CONNECT_TIMEOUT_MS)
    );
    const connectWork = async () => {
      let cid = companyId;
      if (!cid) {
        const { data: { user }, error: userErr } = await supabase.auth.getUser();
        if (userErr) throw new Error(`Auth error: ${userErr.message}`);
        if (!user) throw new Error("Please log in and try again.");
        const { data: role, error: roleErr } = await supabase
          .from("user_roles")
          .select("company_id")
          .eq("user_id", user.id)
          .limit(1)
          .maybeSingle();
        if (roleErr) throw new Error(`Could not load company: ${roleErr.message}`);
        if (!role?.company_id) throw new Error("Your account has no company linked. Complete signup or contact support.");
        cid = role.company_id;
        setCompanyId(cid);
      }
      const { error } = await supabase
        .from("company_integrations")
        .upsert(
          { company_id: cid, provider: "housecall_pro", api_key: key },
          { onConflict: "company_id,provider" }
        );
      if (error) {
        const detail = error.details ? ` (${error.details})` : "";
        const code = error.code ? ` [${error.code}]` : "";
        throw new Error(`${error.message}${detail}${code}`);
      }
    };
    try {
      await Promise.race([connectWork(), timeoutPromise]);
      setHcpConnected(true);
      setHcpApiKey("");
      toast({ title: "Connected", description: "Housecall Pro is now connected. Scheduled calls will sync to HCP." });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Connection failed.";
      toast({ title: "Error", description: msg, variant: "destructive" });
      console.error("HCP connect error:", e);
    } finally {
      setHcpSaving(false);
    }
  };

  const disconnectHousecallPro = async () => {
    if (!companyId) return;
    setHcpSaving(true);
    try {
      const { error } = await supabase
        .from("company_integrations")
        .update({ api_key: null })
        .eq("company_id", companyId)
        .eq("provider", "housecall_pro");
      if (error) throw error;
      setHcpConnected(false);
      toast({ title: "Disconnected", description: "Housecall Pro has been disconnected." });
    } catch (e) {
      console.error(e);
      toast({ title: "Error", description: e instanceof Error ? e.message : "Failed to disconnect", variant: "destructive" });
    } finally {
      setHcpSaving(false);
    }
  };

  // Saved values for change detection (reset after each save)
  const [savedValues, setSavedValues] = useState({
    organizationName: organizationName,
    autoReminder: true,
    reviewMessage: "",
    reviewResponseMessage: "",
    scheduledCallMessage: scheduledCallMessage,
    callSettingsMessage: callSettingsMessage,
    requestRecordingMessage: requestRecordingMessage,
    reminderMessage: reminderMessage,
    reminderMinutes: reminderMinutes,
    // Call settings (now from context)
    callAutoRecord: callAutoRecord,
    callHideSelfView: callHideSelfView,
    callUseBackCamera: callUseBackCamera,
    // Notification settings (from context)
    notifyOnLinkShare: notifyOnLinkShare,
    notifyAutoRecording: notifyAutoRecording
  });

  // Reset saved values when component mounts or when returning from other pages
  useEffect(() => {
    setSavedValues({
      organizationName: organizationName,
      autoReminder: true,
      reviewMessage: "",
      reviewResponseMessage: "",
      scheduledCallMessage: scheduledCallMessage,
      callSettingsMessage: callSettingsMessage,
      requestRecordingMessage: requestRecordingMessage,
      reminderMessage: reminderMessage,
      reminderMinutes: reminderMinutes,
      callAutoRecord: callAutoRecord,
      callHideSelfView: callHideSelfView,
      callUseBackCamera: callUseBackCamera,
      notifyOnLinkShare: notifyOnLinkShare,
      notifyAutoRecording: notifyAutoRecording
    });
  }, []);

  // Save functions for each section
  const saveBusinessDetails = () => {
    setSavedValues(prev => ({
      ...prev,
      organizationName: organizationName
    }));
    toast({
      description: "Company name saved successfully",
    });
  };

  const saveCallSettings = () => {
    setSavedValues(prev => ({
      ...prev,
      callSettingsMessage: callSettingsMessage,
      callAutoRecord: callAutoRecord,
      callHideSelfView: callHideSelfView,
      callUseBackCamera: callUseBackCamera
    }));
    toast({
      description: "Call settings saved successfully",
    });
  };

  const saveScheduledCalls = () => {
    setSavedValues(prev => ({
      ...prev,
      scheduledCallMessage: scheduledCallMessage,
      reminderMessage: reminderMessage,
      reminderMinutes: reminderMinutes
    }));
    toast({
      description: "Scheduled calls settings saved successfully",
    });
  };

  const saveNotifications = () => {
    setSavedValues(prev => ({
      ...prev,
      notifyOnLinkShare: notifyOnLinkShare,
      notifyAutoRecording: notifyAutoRecording
    }));
    toast({
      description: "Notifications settings saved successfully",
    });
  };

  const saveRequestRecording = () => {
    setSavedValues(prev => ({
      ...prev,
      requestRecordingMessage: requestRecordingMessage,
      autoReminder: autoReminder
    }));
    toast({
      description: "Request recording settings saved successfully",
    });
  };

  const saveReviewRequest = () => {
    setSavedValues(prev => ({
      ...prev,
      reviewMessage: reviewMessage
    }));
    toast({
      description: "Review request settings saved successfully",
    });
  };

  const saveRequestReview = () => {
    setSavedValues(prev => ({
      ...prev,
      reviewResponseMessage: reviewResponseMessage
    }));
    toast({
      description: "Request review settings saved successfully",
    });
  };

  // Change detection functions
  const hasBusinessDetailsChanged = () => {
    return organizationName !== savedValues.organizationName;
  };

  const hasReviewRequestChanged = () => {
    return reviewMessage !== savedValues.reviewMessage;
  };

  const hasScheduledCallsChanged = () => {
    return scheduledCallMessage !== savedValues.scheduledCallMessage ||
           reminderMessage !== savedValues.reminderMessage ||
           reminderMinutes !== savedValues.reminderMinutes;
  };

  const hasCallSettingsChanged = () => {
    return callSettingsMessage !== savedValues.callSettingsMessage ||
           callAutoRecord !== savedValues.callAutoRecord ||
           callHideSelfView !== savedValues.callHideSelfView ||
           callUseBackCamera !== savedValues.callUseBackCamera;
  };

  const hasNotificationsChanged = () => {
    return notifyOnLinkShare !== savedValues.notifyOnLinkShare ||
           notifyAutoRecording !== savedValues.notifyAutoRecording;
  };

  const hasRequestRecordingChanged = () => {
    return requestRecordingMessage !== savedValues.requestRecordingMessage ||
           autoReminder !== savedValues.autoReminder;
  };

  const hasRequestReviewChanged = () => {
    return reviewResponseMessage !== savedValues.reviewResponseMessage;
  };

  const hasWorkingHoursChanged = () => {
    if (!workingHoursPersisted) return true;
    return JSON.stringify(workingHours) !== JSON.stringify(savedWorkingHours);
  };

  const saveWorkingHours = async () => {
    try {
      const current = await loadOrCreateUserSettings();
      await saveUserSettings({
        extras: {
          ...(current.extras || {}),
          working_hours: workingHours,
        },
      });
      setSavedWorkingHours(workingHours);
      setWorkingHoursPersisted(true);
      const hasEnabledDay = Object.values(workingHours.schedule).some((d) => d.enabled);
      toast({
        description: hasEnabledDay
          ? "Working hours saved. Schedule a Call is now available on your public intake link."
          : "Working hours saved. Enable at least one day to allow customers to schedule calls.",
      });
    } catch {
      toast({ title: "Error", description: "Failed to save working hours", variant: "destructive" });
    }
  };

  const settingsSections: SettingsSection[] = [
    {
      id: "business-details",
      title: "Business Details",
      hasChanges: hasBusinessDetailsChanged,
      onSave: saveBusinessDetails,
      content: (
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="org-name">Company Name</Label>
            <Input
              id="org-name"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder="Enter company name"
            />
            <p className="text-xs text-muted-foreground">
              Changing this will affect all users/members in your organization
            </p>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={saveBusinessDetails}
              disabled={!hasBusinessDetailsChanged()}
              variant={hasBusinessDetailsChanged() ? "default" : "outline"}
              className="min-w-64 w-64"
            >
              Save Changes
            </Button>
          </div>

          {user?.id && (
            <div className="pt-4 border-t">
              <IntakeLinkCard contractorId={user.id} />
            </div>
          )}
        </CardContent>
      )
    },
    {
      id: "working-hours",
      title: "Public Intake — Working Hours",
      hasChanges: hasWorkingHoursChanged,
      onSave: saveWorkingHours,
      content: (
        <CardContent className="space-y-6">
          <WorkingHoursSettings
            value={workingHours}
            onChange={setWorkingHours}
            persisted={workingHoursPersisted}
          />
          <div className="flex justify-end">
            <Button
              onClick={saveWorkingHours}
              disabled={!hasWorkingHoursChanged()}
              variant={hasWorkingHoursChanged() ? "default" : "outline"}
              className="min-w-64 w-64"
            >
              Save Changes
            </Button>
          </div>
        </CardContent>
      )
    },
    {
      id: "start-live-video-call-settings",
      title: "Start a Live Video Call",
      hasChanges: hasCallSettingsChanged,
      onSave: saveCallSettings,
      content: (
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="contact-msg">Message to Contact</Label>
            <Textarea
              id="contact-msg"
              value={callSettingsMessage}
              onChange={(e) => setCallSettingsMessage(e.target.value)}
              placeholder={`Hi {FirstName}, we want to start a live video call with you. Please click this link to join the call:`}
              rows={4}
              autoCorrect="on"
              spellCheck="true"
            />
            <p className="text-xs text-muted-foreground">
              Use tokens: {'{FirstName}'}, {'{date}'}, {'{time}'}, {'{organization}'} or {'{CompanyName}'}, {'{timeZone}'}
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Automatically start Recording</Label>
                <p className="text-xs text-muted-foreground">
                  Automatically starts recording when customer joins the call
                </p>
              </div>
              <Switch
                checked={callAutoRecord}
                onCheckedChange={setCallAutoRecord}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Hide Self View (Portrait head Camera Off)</Label>
              <Switch
                checked={callHideSelfView}
                onCheckedChange={setCallHideSelfView}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Default to back/rear Camera when joining</Label>
              <Switch
                checked={callUseBackCamera}
                onCheckedChange={setCallUseBackCamera}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button 
              onClick={saveCallSettings}
              disabled={!hasCallSettingsChanged()}
              variant={hasCallSettingsChanged() ? "default" : "outline"}
              className="min-w-64 w-64"
            >
              Save Changes
            </Button>
          </div>
        </CardContent>
      )
    },
    {
      id: "scheduled-calls",
      title: "Scheduled Calls",
      hasChanges: hasScheduledCallsChanged,
      onSave: saveScheduledCalls,
      content: (
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="contact-message">Message to Contact</Label>
            <Textarea
              id="contact-message"
              value={scheduledCallMessage}
              onChange={(e) => setScheduledCallMessage(e.target.value)}
              placeholder={`Hi {FirstName}, you have been invited to have a video call at {time} on {date}. Join the meeting by clicking this link:`}
              rows={4}
              autoCorrect="on"
              spellCheck="true"
            />
            <p className="text-xs text-muted-foreground">
              Use tokens: {'{FirstName}'}, {'{date}'}, {'{time}'}, {'{organization}'} or {'{CompanyName}'}, {'{timeZone}'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reminder-message">Upcoming Appointment Reminder Message</Label>
            <Textarea
              id="reminder-message"
              value={reminderMessage}
              onChange={(e) => setReminderMessage(e.target.value)}
              placeholder="Hi {FirstName}! This is a friendly reminder that your video call is scheduled to start in 30 minutes. Please be ready to join at:"
              rows={4}
              autoCorrect="on"
              spellCheck="true"
            />
            <p className="text-xs text-muted-foreground">
              Use tokens: {'{FirstName}'}, {'{date}'}, {'{time}'}, {'{scheduled time}'}, {'{organization}'}, {'{timeZone}'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reminder-timing">Reminder Timing</Label>
            <Select value={reminderMinutes.toString()} onValueChange={(value) => setReminderMinutes(parseInt(value))}>
              <SelectTrigger>
                <SelectValue placeholder="Select reminder timing" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 60 }, (_, i) => i + 1).map((minutes) => (
                  <SelectItem key={minutes} value={minutes.toString()}>
                    {minutes} minute{minutes !== 1 ? 's' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end">
            <Button 
              onClick={saveScheduledCalls}
              disabled={!hasScheduledCallsChanged()}
              variant={hasScheduledCallsChanged() ? "default" : "outline"}
              className="min-w-64 w-64"
            >
              Save Changes
            </Button>
          </div>
        </CardContent>
      )
    },
    {
      id: "request-recording",
      title: "Request Recording",
      hasChanges: hasRequestRecordingChanged,
      onSave: saveRequestRecording,
      content: (
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="recording-message">Message to Contact</Label>
            <Textarea
              id="recording-message"
              value={requestRecordingMessage}
              onChange={(e) => setRequestRecordingMessage(e.target.value)}
              placeholder={`Hi {FirstName}, you have been sent a link to record your video. Simply click on the link, record, and hit submit.`}
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              Use tokens: {'{FirstName}'}, {'{date}'}, {'{time}'}, {'{organization}'} or {'{CompanyName}'}, {'{timeZone}'}
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label>Automatically set Reminder</Label>
              <p className="text-xs text-muted-foreground">
                When turned ON, a message will automatically remind every 24 hours that the recording is ready for their live review.
              </p>
            </div>
            <Switch 
              checked={autoReminder} 
              onCheckedChange={setAutoReminder}
            />
          </div>

          <div className="flex justify-end">
            <Button 
              onClick={saveRequestRecording}
              disabled={!hasRequestRecordingChanged()}
              variant={hasRequestRecordingChanged() ? "default" : "outline"}
              className="min-w-64 w-64"
            >
              Save Changes
            </Button>
          </div>
        </CardContent>
      )
    },
    {
      id: "notifications",
      title: "Notifications & Other",
      hasChanges: hasNotificationsChanged,
      onSave: saveNotifications,
      content: (
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Send a text message when a link is shared within a new contact</Label>
              <Switch
                checked={notifyOnLinkShare}
                onCheckedChange={setNotifyOnLinkShare}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Automatically send email notifications when recordings are ready for review</Label>
              <Switch
                checked={notifyAutoRecording}
                onCheckedChange={setNotifyAutoRecording}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={saveNotifications}
              disabled={!hasNotificationsChanged()}
              variant={hasNotificationsChanged() ? "default" : "outline"}
              className="min-w-64 w-64"
            >
              Save Changes
            </Button>
          </div>
        </CardContent>
      )
    },
    {
      id: "account",
      title: "Account Settings",
      hasChanges: () => false,
      onSave: () => {},
      content: (
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Delete Account</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <Button
                variant="destructive"
                onClick={() => navigate('/delete-account')}
              >
                Delete Account
              </Button>
            </div>
          </div>
        </CardContent>
      )
    },
    {
      id: "integrations",
      title: "Integrations",
      hasChanges: () => false,
      onSave: () => {},
      content: (
        <CardContent className="space-y-6">
          <GoogleCalendarIntegration />
          {/* Housecall Pro – Workflow A: Schedule A Call */}
          <div className="flex flex-col gap-4 p-4 border rounded-lg">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                HCP
              </div>
              <div>
                <h3 className="font-medium">Housecall Pro</h3>
                <p className="text-sm text-muted-foreground">
                  Sync scheduled video calls to Housecall Pro as jobs (Schedule A Call)
                </p>
              </div>
            </div>
            {hcpConnected ? (
              <div className="flex items-center justify-between">
                <Badge variant="default" className="bg-green-600">Connected</Badge>
                <Button variant="outline" onClick={disconnectHousecallPro} disabled={hcpSaving}>
                  {hcpSaving ? "Disconnecting…" : "Disconnect"}
                </Button>
              </div>
            ) : (
              <div className="grid gap-3 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="hcp-api-key">API Key</Label>
                  <Input
                    id="hcp-api-key"
                    type="password"
                    placeholder="Paste your API key from HCP Dashboard → App Store → API → Generate Key"
                    value={hcpApiKey}
                    onChange={(e) => setHcpApiKey(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground">
                    Get your key in Housecall Pro: App Store → API → Generate Key. No partnership or OAuth required.
                  </p>
                </div>
                <Button onClick={connectHousecallPro} disabled={hcpSaving}>
                  {hcpSaving ? "Connecting…" : "Connect Housecall Pro"}
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center text-white font-bold">
                P
              </div>
              <div>
                <h3 className="font-medium">Podium</h3>
                <p className="text-sm text-muted-foreground">Connect your Podium account</p>
              </div>
            </div>
            <Button variant="outline" disabled>
              Uninstall
            </Button>
          </div>

          <div className="text-center py-8 border-2 border-dashed border-muted rounded-lg">
            <div className="w-12 h-12 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
              <Upload className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground mb-4">More integrations coming soon</p>
            <Button variant="outline" disabled>Browse Integrations</Button>
          </div>
        </CardContent>
      )
    }
  ];

  // Check if any section has unsaved changes
  const hasAnyUnsavedChanges = () => {
    return settingsSections.some(section => section.hasChanges());
  };

  // Handle navigation with unsaved changes
  const handleNavigation = (path: string) => {
    if (hasAnyUnsavedChanges()) {
      setPendingNavigation(path);
      setShowExitDialog(true);
    } else {
      navigate(path);
    }
  };

  const handleSaveAndExit = () => {
    // Save all sections with changes
    settingsSections.forEach(section => {
      if (section.hasChanges()) {
        section.onSave();
      }
    });
    
    setShowExitDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const handleExitWithoutSaving = () => {
    setShowExitDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  // Browser navigation warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasAnyUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasAnyUnsavedChanges]);

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">
      {/* Breadcrumb */}
      <div className="flex items-center text-sm text-muted-foreground">
        <button 
          onClick={() => handleNavigation("/")}
          className="hover:text-primary transition-colors cursor-pointer"
        >
          Home
        </button>
        <span className="mx-2">›</span>
        <span className="text-primary">
          Settings
        </span>
      </div>

      <div className="max-w-4xl">
        <h1 className="text-2xl font-bold mb-6">Settings</h1>

        <Accordion type="single" collapsible className="space-y-4">
          {settingsSections.map((section) => (
            <AccordionItem key={section.id} value={section.id}>
              <Card>
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center justify-between w-full">
                    <span className="font-medium">{section.title}</span>
                    {section.hasChanges() && (
                      <div className="w-2 h-2 bg-primary rounded-full mr-2"></div>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {section.content}
                </AccordionContent>
              </Card>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes that will be lost if you leave this page. 
              Would you like to save your changes before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleExitWithoutSaving}>
              Exit without saving
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveAndExit}>
              Save and exit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SettingsPage;
